import * as Sentry from "@sentry/react";
import { useCallback, useMemo, useState } from "react";
import useProviderDetails from "../useProviderDetails";
import { ResearchChatProvider, useResearchChat } from "./useResearchChat";
import UserAvatar from "../../common/unhealthie/UserAvatar";
import ResearchAssistantAvatar from "./ResearchAssistantAvatar";
import {
  RESEARCH_ASSISTANT_DISCLAIMER,
  RESEARCH_ASSISTANT_SENDER_ID,
  RESEARCH_ASSISTANT_SENDER_NAME,
} from "./Constants";
import { renderMessageWithNoCitations } from "./messageParsers";
import classNames from "classnames";
import PageLoadingSpinner from "../../common/unhealthie/PageLoadingSpinner";
import Button from "../../common/unhealthie/Button";
import { RepeatIcon } from "../../common/unhealthie/fancyIcons";
import HoverMenu from "./HoverMenu";
import { provideFeedbackOnResearchAssistantMessage } from "../../fay/Api";
import { NutritionResearchFeedbackRating } from "../../fay/generated";
import Conversation, {
  ThreadMessage,
} from "../../common/unhealthie/chat/Conversation";
import { TextMessagePreview } from "../../common/unhealthie/chat/ConversationMessagePreview";
import ConversationHeader from "../../common/unhealthie/chat/ConversationHeader";
import ConversationChatBox from "../../common/unhealthie/chat/ConversationChatBox";
import ConversationMessageGroup from "../../common/unhealthie/chat/ConversationMessageGroup";
import { useFeatureFlags } from "../../common/unhealthie/useFeatureFlags";
import { ConversationFrame } from "../../common/unhealthie/chat/ConversationFrame";

type StreamEvent = {
  type: "text_delta" | "run_error" | "failed_to_save_message" | "run_complete";
  data?: string;
  message?: string;
};

const ResearchChatContainer = () => {
  return (
    <ResearchChatProvider>
      <ResearchChat />
    </ResearchChatProvider>
  );
};

const ResearchChat = () => {
  const { providerDetails } = useProviderDetails("me");

  const {
    threadId,
    threadMessages,
    hasMoreMessages,
    fetchLatestThreadMessage,
    fetchPreviousThreadMessages,
    sendNutritionResearchMessage,
    streamNutritionResearchThread,
    runNutritionResearchThread,
    isRunningThread,
    isSendingMessage,
    runThreadError,
    getMessageError,
    sendMessageError,
  } = useResearchChat();

  const [message, setMessage] = useState<string>("");
  const [streamedAssistantResponse, setStreamedAssistantResponse] = useState<
    string | null
  >(null);
  const [streamError, setStreamError] = useState<Error | null>(null);
  const [isLoadingAssistantResponse, setIsLoadingAssistantResponse] =
    useState<boolean>(false);

  const { NUTRITION_RESEARCH_STREAMING } = useFeatureFlags({ overrides: {} });

  const conversationMessages: ThreadMessage[] = useMemo(() => {
    if (!threadMessages || !providerDetails) {
      return [];
    }
    const messages: ThreadMessage[] = threadMessages.map(
      ({ message_id, content, role, created_at }, _) => {
        const messageContent = renderMessageWithNoCitations({
          message: content,
        });

        return {
          className: "tw-flex tw-flex-col tw-group",
          chatMessageId: message_id,
          sentAt: new Date(created_at),
          format: "markdown",
          Avatar:
            role === "user"
              ? () => <UserAvatar user={providerDetails} size="large" />
              : () => <ResearchAssistantAvatar className="tw-h-10 tw-w-10" />,
          content: messageContent,
          senderDisplayName:
            role === "user"
              ? providerDetails.preferred_first_name
              : RESEARCH_ASSISTANT_SENDER_NAME,
          senderId:
            role === "user"
              ? providerDetails.provider_id
              : RESEARCH_ASSISTANT_SENDER_ID,
          DisplayComponent: () => (
            <>
              {role === "assistant" && (
                <div>
                  <span className="tw-font-medium tw-italic tw-leading-8 tw-text-neutral-400">
                    {RESEARCH_ASSISTANT_DISCLAIMER}
                  </span>
                </div>
              )}
              {role === "assistant" && (
                <div className="tw-hidden tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100 sm:tw-block">
                  <HoverMenu
                    onCopy={() => navigator.clipboard.writeText(messageContent)}
                    onThumbsUp={() =>
                      provideFeedbackOnResearchAssistantMessage({
                        messageId: message_id,
                        feedbackRating:
                          NutritionResearchFeedbackRating.Positive,
                      })
                    }
                    onThumbsDown={() =>
                      provideFeedbackOnResearchAssistantMessage({
                        messageId: message_id,
                        feedbackRating:
                          NutritionResearchFeedbackRating.Negative,
                      })
                    }
                  />
                </div>
              )}
            </>
          ),
          preview: () => (
            <TextMessagePreview content={content} isHtml={false} />
          ),
        };
      },
    );

    if (
      !streamedAssistantResponse &&
      (isRunningThread || isLoadingAssistantResponse)
    ) {
      messages.push({
        chatMessageId: "loading",
        sentAt: new Date(),
        format: "markdown",
        Avatar: () => <ResearchAssistantAvatar className="tw-h-10 tw-w-10" />,
        DisplayComponent: () => (
          <div className="tw-font-medium tw-text-neutral-400">
            Searching... this may take a few seconds.
          </div>
        ),
        content: "",
        senderDisplayName: RESEARCH_ASSISTANT_SENDER_NAME,
        senderId: RESEARCH_ASSISTANT_SENDER_ID,
      });
    }

    return messages;
  }, [
    providerDetails,
    threadMessages,
    isRunningThread,
    streamedAssistantResponse,
    isLoadingAssistantResponse,
  ]);

  const runThreadAndGetResponse = async () => {
    if (!threadId) {
      return;
    }
    setIsLoadingAssistantResponse(true);
    try {
      await runNutritionResearchThread(threadId);
      await fetchLatestThreadMessage(threadId);
    } catch (e) {
      Sentry.captureException(e);
    }
    setIsLoadingAssistantResponse(false);
  };

  const streamThreadAndGetResponse = async () => {
    setStreamedAssistantResponse(null);
    setStreamError(null);
    setIsLoadingAssistantResponse(true);
    if (!threadId) {
      return;
    }
    try {
      const stream = await streamNutritionResearchThread(threadId);
      const reader = stream.getReader();
      let runningStreamText = "";

      while (true) {
        // done might not be truthy if an error has occurred in the middle of streaming
        const { done, value } = await reader.read();
        if (done) {
          reader.cancel();
          break;
        }

        if (!value) {
          continue;
        }

        const { chunkText, isDoneStreaming } = processStreamChunk(value);
        runningStreamText += chunkText;
        setStreamedAssistantResponse(runningStreamText);

        if (isDoneStreaming) {
          reader.cancel();
          break;
        }
      }
      // message has finished streaming by this point.
      // fetch the saved message from the backend to keep source of truth in sync just in case
      await fetchLatestThreadMessage(threadId);
      setStreamedAssistantResponse(null);
    } catch (e) {
      Sentry.captureException(e);
    }
    setIsLoadingAssistantResponse(false);
  };

  const handleStreamEvent = useCallback(
    (event: StreamEvent) => {
      const { type, data } = event;
      switch (type) {
        case "text_delta":
          return { text: data || "", isDoneStreaming: false };
        case "run_error":
        case "failed_to_save_message":
          setStreamError(new Error("There was an error generating a response"));
          return { text: "", isDoneStreaming: true };
        case "run_complete":
          return { text: "", isDoneStreaming: true };
        default:
          setStreamError(new Error("There was an error generating a response"));
          console.error("Unknown event type", type);
          return { text: "", isDoneStreaming: true };
      }
    },
    [setStreamError],
  );

  const processStreamChunk = useCallback(
    (chunk: Uint8Array) => {
      // it's possible we receive multiple events in a single chunk here.
      // the backend separates chunks by newlines in this case (NDJSON), so we can split
      // on newlines and process each event individually.
      const rawDecodedEvents = new TextDecoder("utf-8")
        .decode(chunk)
        .split("\n")
        .filter(Boolean);

      let chunkText = "";
      for (const rawDecodedEvent of rawDecodedEvents) {
        const event = JSON.parse(rawDecodedEvent);
        const { text, isDoneStreaming } = handleStreamEvent(event);
        chunkText += text;

        if (isDoneStreaming) return { chunkText, isDoneStreaming };
      }

      return { chunkText, isDoneStreaming: false };
    },
    [handleStreamEvent],
  );

  const onMessageSend = async () => {
    const toSend = message.trim();
    if (toSend.length === 0 || !threadId) {
      return;
    }
    await sendNutritionResearchMessage(threadId, toSend);
    await fetchLatestThreadMessage(threadId);
    setMessage("");

    if (NUTRITION_RESEARCH_STREAMING.value === true) {
      await streamThreadAndGetResponse();
    } else {
      await runThreadAndGetResponse();
    }
  };

  const loadMoreChatHistoryForSelectedConversation = () => {
    if (!threadId) {
      return;
    }
    fetchPreviousThreadMessages(threadId);
  };

  return (
    <ConversationFrame>
      <ConversationFrame.Header>
        <ConversationHeader
          respondentDisplayName={RESEARCH_ASSISTANT_SENDER_NAME}
          RespondentAvatar={() => <ResearchAssistantAvatar />}
        />
      </ConversationFrame.Header>
      <ConversationFrame.Body>
        {!threadMessages || !providerDetails ? (
          <PageLoadingSpinner />
        ) : (
          <Conversation
            messages={conversationMessages}
            hasMoreMessages={hasMoreMessages}
            loadMoreChatHistoryForSelectedConversation={
              loadMoreChatHistoryForSelectedConversation
            }
            error={sendMessageError || getMessageError}
            // this is sort of a hack to display the assistant's response as it's being streamed
            // token by token. React seems to struggle rerendering fast enough if we pass
            // the streamedAssistantResponse to the conversationMessages directly
            // (it has to recompute the entire conversationMessages array for each new token)
            DisplayComponent={() =>
              streamedAssistantResponse && (
                <ConversationMessageGroup
                  messagesInDay={[
                    {
                      chatMessageId: "streamed",
                      sentAt: new Date(),
                      format: "markdown",
                      Avatar: () => (
                        <ResearchAssistantAvatar className="tw-h-10 tw-w-10" />
                      ),
                      content: renderMessageWithNoCitations({
                        message: streamedAssistantResponse,
                      }),
                      senderDisplayName: RESEARCH_ASSISTANT_SENDER_NAME,
                      senderId: RESEARCH_ASSISTANT_SENDER_ID,
                    },
                  ]}
                />
              )
            }
          />
        )}
      </ConversationFrame.Body>
      <ConversationFrame.Footer>
        {runThreadError || streamError ? (
          <div className={styles.runThreadErrorRegenerate}>
            <div>{runThreadError?.message || streamError?.message}</div>
            <Button
              Icon={RepeatIcon}
              type="default"
              onClick={runThreadAndGetResponse}
            >
              Regenerate
            </Button>
          </div>
        ) : (
          <ConversationChatBox
            message={message}
            placeholder={"Ask Research Assistant anything..."}
            onMessageChange={setMessage}
            onSend={onMessageSend}
            isSendingMessage={isSendingMessage}
            disabled={
              isSendingMessage ||
              !threadMessages ||
              !providerDetails ||
              isRunningThread ||
              isLoadingAssistantResponse
            }
            sendDisabled={message.trim().length === 0}
          />
        )}
      </ConversationFrame.Footer>
    </ConversationFrame>
  );
};

const styles = {
  runThreadErrorRegenerate: classNames(
    "tw-flex tw-flex-col",
    "tw-items-center",
    "tw-justify-center",
    "tw-gap-3",
  ),
};

export default ResearchChatContainer;
